import React, { useState, useEffect, useCallback } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useDeal } from "@/hooks/useDeals";
import { useItems } from "@/hooks/useItems";
import { useCatalog } from "@/hooks/useCatalog";
import { useNotes } from "@/hooks/useNotes";
import { supabase } from "@/integrations/supabase/client";
import { useTwilio } from "@/contexts/TwilioContext";
import { useAppUsers } from "@/hooks/useAppUsers";
import { useSalesUsers } from "@/hooks/useSalesUsers";
import { useCancelDeal } from "@/hooks/deals/useCancelDeal";
import { useRevertCancelDeal } from "@/hooks/deals/useRevertCancelDeal";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { ArrowLeft, Edit, Mail, Phone, Building, MoreVertical, Globe, Calendar, DollarSign, UserCog, TrendingUp, Package, Plus, Trash2, Edit2, CreditCard, Copy, AlertCircle, RefreshCw, ChevronDown, Ban, Pencil, UserPlus, Search, Wallet, RotateCcw, ArrowUpRight, ClipboardCheck, Video, Zap, Users, MessageCircle } from "lucide-react";
import { useNavigate, useLocation } from "react-router-dom";
import { useGoBack } from "@/hooks/useGoBack";
import { createPageUrl, formatCurrency } from "@/utils";
import { Skeleton } from "@/components/ui/skeleton";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";

import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { format } from "date-fns";
import { safeFormat } from '@/lib/safeDate';
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { formatPhoneNumber, getFullPhoneE164 } from "@/lib/utils";
import PaymentPlanV2 from "../components/deals/PaymentPlanV2";
import ProductsEditModal from "../components/deals/ProductsEditModal";
import { DealOverview, NotesSection } from "@/components/deals/DealOverview";
import { MeetingsTab } from "@/components/deals/MeetingsTab";
import { PaymentsTab } from "@/components/deals/PaymentsTab";
import { ContractsTab, useHasActiveContract } from "@/components/deals/ContractsTab";
import { DealNotesMarkdown } from "@/components/deals/DealNotesMarkdown";
import CallsTab from "@/components/contacts/CallsTab";
import { ChatCore, DealChatHeader, DrawerChatLayout } from "@/features/chat";
import DealTasksTab from "@/components/deals/DealTasksTab";
import { DealActivityTimeline } from "@/components/deals/DealActivityTimeline";
import { TabGroup } from "@/components/common/TabGroup";
import { toast } from "sonner";
import { DealStageProgress } from "@/components/deals/DealStageProgress";
import CancelDealModal from "@/components/deals/CancelDealModal";
import RevertCancelModal from "@/components/deals/RevertCancelModal";
import ItemPeopleEditor from "@/components/deals/ItemPeopleEditor";
import AddItemModal from "@/components/deals/AddItemModal";
import DealClosingValidation from "@/components/deals/DealClosingValidation";
import ProspectingValidationModal from "@/components/deals/ProspectingValidationModal";
import { validateProspectingRequirements } from "@/lib/prospectingValidation";
import ItemEligibilityForm from "@/components/deals/ItemEligibilityForm";
// REMOVIDO: QualificationTab (mantido no codebase para reativação futura)
// import { QualificationTab } from "@/components/deals/QualificationTab";
// TEMPORARIAMENTE DESATIVADO - Aguardando decisão sobre funcionalidades
// import { InteractionsTab } from "@/components/deals/InteractionsTab";
// import { EligibilityTab } from "@/components/deals/EligibilityTab";
import ItemPeoplePreview from "@/components/deals/ItemPeoplePreview";
// LostReasonModal import removed - using unified AlertDialog modal (showLostReasonDialog)
import { PostMeetingModal } from "@/components/deals/PostMeetingModal";
import { QualificationRequiredModal } from "@/components/deals/QualificationRequiredModal";
import { QualificationModal } from "@/components/deals/QualificationModal";
import { validateDealForScheduled } from "@/hooks/useStageTransition";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { useUserPermissions } from "@/hooks/useUserPermissions";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar as CalendarUI } from "@/components/ui/calendar";
import { SOURCE_TYPES, CHANNEL_TYPES } from "@/lib/sourceChannelTypes";
import { useTranslation } from "react-i18next";
import { ContactSearchCombobox } from "@/components/common/ContactSearchCombobox";
import { Checkbox } from "@/components/ui/checkbox";
import { Switch } from "@/components/ui/switch";
import { Separator } from "@/components/ui/separator";
import { COUNTRY_CODES } from "@/lib/countryCodes";
import { CardSetupShareModal } from "@/components/shared/CardSetupShareModal";
import { ChargeAdhocModal } from "@/components/shared/ChargeAdhocModal";
import { PaymentLinkShareModal } from "@/components/shared/PaymentLinkShareModal";
import { BusinessUnitSelector } from "@/components/finance/BusinessUnitSelector";
import { getBusinessUnitWithFlag } from "@/lib/businessUnits";
import { DealTagsDisplay } from "@/components/deals/DealTagsDisplay";
import { AssignmentHistoryTab } from "@/components/deals/AssignmentHistoryTab";
import { ScheduleMeetingModal } from "@/components/agenda/ScheduleMeetingModal";
import { useDailyRing } from "@/contexts/DailyRingContext";
import { DirectCallModal } from "@/components/calls/DirectCallModal";


// Currency formatting helpers for USD
const formatCurrencyInput = (value) => {
  const digits = value.replace(/\D/g, '');
  const number = parseInt(digits) / 100;
  if (isNaN(number) || number === 0) return '';
  return number.toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  });
};

const parseCurrencyInput = (value) => {
  const digits = value.replace(/\D/g, '');
  return parseInt(digits) / 100 || 0;
};

const stageColors = {
  lead: "bg-slate-100 text-slate-700 border-slate-200",
  qualified: "bg-blue-100 text-blue-700 border-blue-200",
  proposal: "bg-purple-100 text-purple-700 border-purple-200",
  negotiation: "bg-amber-100 text-amber-700 border-amber-200",
  closed_won: "bg-emerald-100 text-emerald-700 border-emerald-200",
  closed_lost: "bg-rose-100 text-rose-700 border-rose-200"
};
const sourceColors = {
  website: "bg-blue-100 text-blue-700",
  referral: "bg-purple-100 text-purple-700",
  social_media: "bg-pink-100 text-pink-700",
  email_campaign: "bg-indigo-100 text-indigo-700",
  phone_call: "bg-emerald-100 text-emerald-700",
  walk_in: "bg-amber-100 text-amber-700",
  existing_client: "bg-teal-100 text-teal-700",
  other: "bg-slate-100 text-slate-700"
};

const lostReasonKeys = [
  'alreadyClient',
  'fearInsecurity',
  'ghostedNoResponse',
  'indirectInquiry',
  'invalidLead',
  'needsApproval',
  'noBudget',
  'noUrgency',
  'notEligible',
  'notInterested',
  'seasonalTiming',
  'wentWithCompetitor',
  'wrongClick'
];
export default function DealDetail() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const goBack = useGoBack('/deals');
  // Support both path params (/deals/:id) and query params (?id=)
  const pathParts = location.pathname.split('/');
  const pathId = pathParts[pathParts.length - 1];
  const urlParams = new URLSearchParams(location.search);
  const queryId = urlParams.get('id');
  const initialTab = urlParams.get('tab') || 'details';
  const dealId = queryId || (pathId && pathId !== 'deals' ? pathId : null);
  
  // Build lostReasons array with translations
  const lostReasons = lostReasonKeys.map(key => ({
    value: key,
    label: t(`lostReasons.${key}`)
  }));
  const {
    deal,
    loading: dealLoading,
    loadDeal
  } = useDeal(dealId);
  const queryClient = useQueryClient();
const {
  items,
  loading: itemsLoading,
  createItem,
  updateItem,
  deleteItem,
  loadItems
} = useItems(dealId);
  const {
    items: catalogItems,
    loading: catalogLoading
  } = useCatalog();
  const {
    notes,
    loading: notesLoading,
    createNote,
    deleteNote
  } = useNotes({ dealId });
  const [contact, setContact] = useState(null);
  const [assignedUser, setAssignedUser] = useState(null);
  const [sdrUser, setSdrUser] = useState(null);
  const [creatorUser, setCreatorUser] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [activeTab, setActiveTab] = useState(initialTab);
  const [user, setUser] = useState(null);
  const { users } = useAppUsers();
  const { salesUsers, isLoading: isLoadingSalesUsers } = useSalesUsers();
  const [showAssignDialog, setShowAssignDialog] = useState(false);
  const [showSalesTeamDialog, setShowSalesTeamDialog] = useState(false);
  const [showStageDialog, setShowStageDialog] = useState(false);
  const [newNoteContent, setNewNoteContent] = useState("");
  const [showProductsModal, setShowProductsModal] = useState(false);
  const [showAddItemDialog, setShowAddItemDialog] = useState(false);
  const [showLostReasonDialog, setShowLostReasonDialog] = useState(false);
  const [lostReason, setLostReason] = useState('');
  const [contactVerificationStatus, setContactVerificationStatus] = useState('keep');
  const [pendingStage, setPendingStage] = useState(null);
  const [showWonDialog, setShowWonDialog] = useState(false);
  const [closedDateForWon, setClosedDateForWon] = useState(new Date());
  const [closedDateForLost, setClosedDateForLost] = useState(new Date());
  const { isAdmin, isSalesManager, isLoading: permissionsLoading } = useUserPermissions();
  const [moduleSettings, setModuleSettings] = useState({});
  const [isLoadingCard, setIsLoadingCard] = useState(false);
  const {
    makeCall,
    isReady,
    isInitiatingCall
  } = useTwilio();
  const dailyRing = useDailyRing();
  const [showDiscountDialog, setShowDiscountDialog] = useState(false);
  const [discountType, setDiscountType] = useState('none');
  const [discountValue, setDiscountValue] = useState(0);
  const [discountValueDisplay, setDiscountValueDisplay] = useState('');
  const [showDeleteDealDialog, setShowDeleteDealDialog] = useState(false);
  const [isDeletingDeal, setIsDeletingDeal] = useState(false);
  const [paymentSchedule, setPaymentSchedule] = useState([]);
  const [showLegacyProductModal, setShowLegacyProductModal] = useState(false);
  const [showCancelDealModal, setShowCancelDealModal] = useState(false);
  const [showScheduleMeetingModal, setShowScheduleMeetingModal] = useState(false);
  const [showDirectCallModal, setShowDirectCallModal] = useState(false);
  const [showWhatsAppSheet, setShowWhatsAppSheet] = useState(false);
  const [showRevertCancelModal, setShowRevertCancelModal] = useState(false);
  const [showValidationDialog, setShowValidationDialog] = useState(false);
  const [expandedItemId, setExpandedItemId] = useState(null);
  const [itemSubtab, setItemSubtab] = useState({});
  const [editingItemId, setEditingItemId] = useState(null);
  const [showCardActionPopover, setShowCardActionPopover] = useState(false);
  const [showCardSetupModal, setShowCardSetupModal] = useState(false);
  const [cardSetupLink, setCardSetupLink] = useState(null);
  const [isCreatingSetupLink, setIsCreatingSetupLink] = useState(false);
  const [showChargeModal, setShowChargeModal] = useState(false);
  const [showPaymentLinkModal, setShowPaymentLinkModal] = useState(false);
  const [paymentLink, setPaymentLink] = useState(null);
  const [chargeAmount, setChargeAmount] = useState(0);
  const [isCreatingPaymentLink, setIsCreatingPaymentLink] = useState(false);
  const [cancelledPaymentsCount, setCancelledPaymentsCount] = useState(0);
  const [showEditTitleDialog, setShowEditTitleDialog] = useState(false);
  const [editingTitle, setEditingTitle] = useState('');
  // Upsell credits now come directly from deal.transferred_payments_amount (permanent, not from payments table)
  const upsellCreditsTotal = deal?.transferred_payments_amount || 0;
  const cancelDealMutation = useCancelDeal();
  const revertCancelMutation = useRevertCancelDeal();

  // States for new stage transition modals
  // Note: showNewLostReasonModal state removed - using unified showLostReasonDialog
  const [showPostMeetingModal, setShowPostMeetingModal] = useState(false);
  const [showQualificationModal, setShowQualificationModal] = useState(false);
  const [qualificationMissingFields, setQualificationMissingFields] = useState([]);
  const [showFullQualificationModal, setShowFullQualificationModal] = useState(false);

  const formatType = type => {
    if (!type || typeof type !== 'string') return '';
    return type.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };
  useEffect(() => {
    if (!dealId) {
      navigate(createPageUrl("Deals"));
      return;
    }
  }, [dealId, navigate]);
  useEffect(() => {
    const loadAdditionalData = async () => {
      if (!deal) return;
      try {
        // Load contact
        if (deal.contact_id) {
          const {
            data: contactData
          } = await supabase.from('contacts').select('*, contact_addresses:contact_addresses!contact_addresses_contact_id_fkey(*)').eq('id', deal.contact_id).single();
          setContact(contactData);
        }

        // Get current user
        const {
          data: {
            user: authUser
          }
        } = await supabase.auth.getUser();
        if (authUser) {
          const {
            data: userData
          } = await supabase.from('app_users').select('*').eq('auth_user_id', authUser.id).single();
          setUser(userData);
        }

        // Load assigned consultant
        if (deal.sales_consultant) {
          const {
            data: assignedData
          } = await supabase.from('app_users').select('*').eq('id', deal.sales_consultant).single();
          setAssignedUser(assignedData);
        }

        // Load SDR
        if (deal.sales_development) {
          const {
            data: sdrData
          } = await supabase.from('app_users').select('*').eq('id', deal.sales_development).single();
          setSdrUser(sdrData);
        }

        // Load creator user
        if (deal.created_by) {
          const {
            data: creatorData
          } = await supabase.from('app_users').select('*').eq('id', deal.created_by).single();
          setCreatorUser(creatorData);
        }

        // Count cancelled payments for revert modal
        if (deal.deal_status === 'cancelled') {
          const { count } = await supabase
            .from('payments')
            .select('*', { count: 'exact', head: true })
            .eq('deal_id', deal.id)
            .eq('status', 'cancelled');
          setCancelledPaymentsCount(count || 0);
        }
      } catch (error) {
        console.error("Error loading additional data:", error);
      }
    };
    loadAdditionalData();
  }, [deal]);

  // Sync discount states when deal loads
  useEffect(() => {
    if (deal) {
      setDiscountType(deal.discount_type || 'none');
      const currentValue = deal.discount_value || 0;
      setDiscountValue(currentValue);
      
      // Format display value
      if (deal.discount_type === 'amount' && currentValue > 0) {
        setDiscountValueDisplay(currentValue.toLocaleString('en-US', {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2
        }));
      } else {
        setDiscountValueDisplay(currentValue > 0 ? currentValue.toString() : '');
      }
    }
  }, [deal]);

  // Reset display when discount type changes
  useEffect(() => {
    if (discountType === 'none') {
      setDiscountValue(0);
      setDiscountValueDisplay('');
    } else if (discountType === 'percent') {
      setDiscountValueDisplay(discountValue > 0 ? discountValue.toString() : '');
    } else if (discountType === 'amount') {
      if (discountValue > 0) {
        setDiscountValueDisplay(discountValue.toLocaleString('en-US', {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2
        }));
      } else {
        setDiscountValueDisplay('');
      }
    }
  }, [discountType]);

  // Check for legacy deals in PROPOSAL/NEGOTIATING without products - show blocking modal
  // ONLY for the Sales Consultant assigned to the deal
  useEffect(() => {
    if (!dealLoading && !itemsLoading && deal && items !== undefined && user) {
      const isAdvancedStage = deal.stage === 'PROPOSAL' || deal.stage === 'NEGOTIATING';
      const hasNoItems = items.length === 0;
      const isAssignedConsultant = user.id === deal.sales_consultant;
      
      if (isAdvancedStage && hasNoItems && isAssignedConsultant) {
        setShowLegacyProductModal(true);
      } else {
        setShowLegacyProductModal(false);
      }
    }
  }, [deal, items, dealLoading, itemsLoading, user]);

  // Check if deal is closed (WON or LOST)
  const isWon = deal?.stage === 'won' || deal?.stage === 'WON';
  const isLost = deal?.stage === 'lost' || deal?.stage === 'LOST';
  const isClosed = isWon || isLost;

  // When deal is closed:
  // - NOBODY can edit content (Overview, Details, Payments) - not even Admin
  // - Only Admin/SalesManager can CHANGE THE STAGE (to "reopen" the deal)
  const isEditable = !isClosed || (!permissionsLoading && isAdmin === true);  // Admin can always edit
  const canChangeStage = !isClosed || (!permissionsLoading && (isAdmin === true || isSalesManager === true));

  // Block details editing when active contract exists
  const { hasActiveContract, refresh: refreshContractStatus } = useHasActiveContract(dealId);
  const isDetailsEditable = isEditable && !hasActiveContract;

  // State for prospecting validation modal
  const [showProspectingValidation, setShowProspectingValidation] = useState(false);
  const [prospectingMissingFields, setProspectingMissingFields] = useState({ contact: [], deal: [] });

  const handleUpdateStage = async newStage => {
    // NEW: Handle lowercase stages for stage transition modals
    // Validate: contact -> scheduled (lowercase) requires qualification
    if (deal?.stage === 'contact' && newStage === 'scheduled') {
      const validation = await validateDealForScheduled(deal.id);
      if (!validation.valid) {
        setQualificationMissingFields(validation.missingFields);
        setShowQualificationModal(true);
        return;
      }
    }

    // NEW: Handle scheduled -> proposal (lowercase) requires post-meeting modal
    if (deal?.stage === 'scheduled' && newStage === 'proposal') {
      setShowPostMeetingModal(true);
      return;
    }

    // Handle lost (lowercase) - use same modal as LOST
    if (newStage === 'lost') {
      setPendingStage('lost');
      setClosedDateForLost(new Date());
      setShowLostReasonDialog(true);
      setShowStageDialog(false);
      return;
    }

    // NEW: Handle won (lowercase) - validate and open modal
    if (newStage === 'won') {
      if (items.length === 0) {
        toast.error('Não é possível fechar o Deal como WON sem produtos lançados');
        return;
      }
      setPendingStage('won');
      setShowValidationDialog(true);
      return;
    }

    // Legacy uppercase stages are no longer used - all transitions now go through
    // lowercase handlers above (contact->scheduled, scheduled->proposal, lost, won)

    setIsProcessing(true);
    try {
      const updateData = {
        stage: newStage
      };
      
      // Limpar closed_date ao mover deal para fora de WON
      if ((deal.stage === 'won' || deal.stage === 'WON') && 
          newStage !== 'won' && newStage !== 'WON') {
        updateData.closed_date = null;
      }
      
      const { data, error } = await supabase
        .from('deals')
        .update(updateData)
        .eq('id', deal.id)
        .select()
        .single();
      
      if (error) {
        console.error("Error updating stage:", error);
        throw error;
      }
      
      if (!data) {
        console.error("No data returned from update");
        throw new Error("Failed to update deal stage");
      }
      
      await loadDeal();
      setShowStageDialog(false);
      toast.success(`Deal movido para ${newStage.replace(/_/g, ' ')}`);
    } catch (error) {
      console.error("Error updating stage:", error);
      toast.error("Erro ao atualizar stage: " + error.message);
    }
    setIsProcessing(false);
  };

  const handleConfirmWon = async () => {
    // Validar source e channel obrigatórios
    if (!deal.deals_source || deal.deals_source === 'Unselected') {
      toast.error(t('deals.sourceRequiredForWon') || 'Origem da negociação é obrigatória para fechar o deal como Ganho');
      return;
    }
    if (!deal.deals_channel) {
      toast.error(t('deals.channelRequiredForWon') || 'Canal da negociação é obrigatório para fechar o deal como Ganho');
      return;
    }

    setIsProcessing(true);
    try {
      // 1. Delete existing payments without transactions (paid_amount = 0)
      const { error: deleteError } = await supabase
        .from('payments')
        .delete()
        .eq('deal_id', deal.id)
        .eq('paid_amount', 0);
      
      if (deleteError) {
        console.error("Error deleting old payments:", deleteError);
      }
      
      // 2. Create new payments from schedule
      if (paymentSchedule.length > 0) {
        const paymentsToCreate = paymentSchedule.map((payment, index) => ({
          deal_id: deal.id,
          payment_number: `Payment ${index + 1}`,
          installment_number: index + 1,
          payment_type: payment.type === 'deposit' ? 'down_payment' : payment.type,
          description: `${payment.type === 'deposit' ? 'Deposit' : 'Installment'} payment ${payment.number}`,
          amount: payment.amount,
          due_date: safeFormat(payment.due_date, 'yyyy-MM-dd'),
          status: 'pending',
          paid_amount: 0,
          balance: payment.amount,
        }));

        const { error: insertError } = await supabase
          .from('payments')
          .insert(paymentsToCreate);
        
        if (insertError) {
          console.error("Error creating payments:", insertError);
          throw insertError;
        }
      }
      
      // 3. Update deal to WON (use lowercase for consistency with new pipeline)
      const { data, error } = await supabase
        .from('deals')
        .update({
          stage: 'won',
          closed_date: safeFormat(closedDateForWon, 'yyyy-MM-dd')
        })
        .eq('id', deal.id)
        .select()
        .single();
      
      if (error) {
        console.error("Error updating stage:", error);
        throw error;
      }
      
      // 4. Update all items to pending_onboarding (creates onboarding flow)
      const { error: itemsError } = await supabase
        .from('items')
        .update({ status: 'pending_onboarding' })
        .eq('deal_id', deal.id);

      if (itemsError) {
        console.error("Error updating items status:", itemsError);
        toast.error("Aviso: Alguns itens podem não ter sido atualizados para onboarding");
      }
      
      await loadDeal();
      setShowWonDialog(false);
      setPendingStage(null);
      toast.success('Negócio marcado como Ganho!');
    } catch (error) {
      console.error("Error updating stage:", error);
      toast.error("Erro ao atualizar estágio: " + error.message);
    }
    setIsProcessing(false);
  };

  const handleConfirmLost = async () => {
    if (!lostReason) {
      toast.error('Por favor selecione um motivo');
      return;
    }

    setIsProcessing(true);
    try {
      // Use lowercase 'lost' as standard, save to both fields for compatibility
      const { data, error } = await supabase
        .from('deals')
        .update({
          stage: 'lost',
          closed_date: safeFormat(closedDateForLost, 'yyyy-MM-dd'),
          lost_reason: lostReason,
          detailed_lost_reason: lostReason, // Compatibility with new validations
        })
        .eq('id', deal.id)
        .select()
        .single();
      
      if (error) {
        console.error("Error updating stage:", error);
        throw error;
      }

      // Update contact verification status if selected
      if (contactVerificationStatus !== 'keep' && deal.contact_id) {
        const { error: contactError } = await supabase
          .from('contacts')
          .update({ verification_status: contactVerificationStatus })
          .eq('id', deal.contact_id);
        
        if (contactError) {
          console.error("Error updating contact verification:", contactError);
        }
      }
      
      await loadDeal();
      setShowLostReasonDialog(false);
      setLostReason('');
      setContactVerificationStatus('keep');
      setPendingStage(null);
      toast.success('Negócio marcado como Perdido');
    } catch (error) {
      console.error("Error updating stage:", error);
      toast.error("Erro ao atualizar estágio: " + error.message);
    }
    setIsProcessing(false);
  };

  // NOTE: handleConfirmNewLostReason removed - now using unified handleConfirmLost

  // NEW: Handler for post-meeting modal
  const handleConfirmPostMeeting = async (data) => {
    setIsProcessing(true);
    try {
      if (!data.meetingHappened) {
        // No-show: increment counter, DON'T move
        await supabase
          .from('deals')
          .update({
            noshow_count: (deal.noshow_count || 0) + 1,
            meeting_notes: data.noShowReason,
          })
          .eq('id', deal.id);
        
        toast.warning(t('stageTransition.noShowRegistered') || 'No-show registrado');
      } else {
        // Meeting happened: update and move
        await supabase
          .from('deals')
          .update({
            stage: 'proposal',
            meeting_notes: data.meetingNotes,
            proposal_value: data.proposalValue,
            recommended_process: data.recommendedProcess,
          })
          .eq('id', deal.id);
        
        toast.success(t('deals.movedToProposal') || 'Deal movido para Proposta');
      }
      
      await loadDeal();
    } catch (error) {
      console.error("Error updating deal:", error);
      toast.error(t('common.error') + ': ' + error.message);
    } finally {
      setIsProcessing(false);
      setShowPostMeetingModal(false);
    }
  };

  // NEW: Handler for qualification modal
  const handleConfirmQualification = async (data) => {
    setIsProcessing(true);
    try {
      // Upsert qualification
      await supabase
        .from('deal_qualification')
        .upsert({
          deal_id: deal.id,
          urgency: data.urgency,
          objective: data.objective,
        }, { onConflict: 'deal_id' });
      
      // Create meeting if date provided
      if (data.meetingDate) {
        await supabase
          .from('calls')
          .insert({
            deal_id: deal.id,
            contact_id: deal.contact_id,
            channel: 'video',
            direction: 'outbound',
            scheduled_for: data.meetingDate.toISOString(),
            status: 'scheduled',
          });
      }
      
      // Move to scheduled
      await supabase
        .from('deals')
        .update({ stage: 'scheduled' })
        .eq('id', deal.id);
      
      await loadDeal();
      toast.success(t('stageTransition.meetingScheduled') || 'Reunião agendada');
    } catch (error) {
      console.error("Error updating deal:", error);
      toast.error(t('common.error') + ': ' + error.message);
    } finally {
      setIsProcessing(false);
      setShowQualificationModal(false);
    }
  };

  const goToContactDetail = e => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    if (contact?.id) {
      const contactDetailUrl = createPageUrl("ContactDetail") + "?id=" + contact.id;
      window.location.href = contactDetailUrl;
    }
  };
  const handleUpdateProducts = async updatedProductData => {
    setIsProcessing(true);
    try {
      const { data, error } = await supabase
        .from('deals')
        .update(updatedProductData)
        .eq('id', deal.id)
        .select()
        .single();
      
      if (error) {
        console.error("Error updating products:", error);
        throw error;
      }
      
      if (!data) {
        console.error("No data returned from update");
        throw new Error("Failed to update deal products");
      }
      
      setShowProductsModal(false);
      await loadDeal();
      toast.success("Produtos atualizados com sucesso");
    } catch (error) {
      console.error("Error updating products:", error);
      toast.error("Erro ao atualizar produtos: " + error.message);
    }
    setIsProcessing(false);
  };

  const handleGetCard = async () => {
    setIsLoadingCard(true);
    try {
      const { data, error } = await supabase.functions.invoke('stripe-create-checkout', {
        body: { deal_id: dealId }
      });
      
      if (error) throw error;
      
      toast.success("Payment link gerado com sucesso!");
      await loadDeal(); // Recarrega para mostrar o link
    } catch (error) {
      console.error('Error generating payment link:', error);
      toast.error("Erro ao gerar payment link: " + error.message);
    } finally {
      setIsLoadingCard(false);
    }
  };

  const handleSaveCardClick = async () => {
    setShowCardActionPopover(false);
    setIsCreatingSetupLink(true);
    
    try {
      if (!deal?.contact_id) {
        throw new Error("Contato não encontrado nesta negociação");
      }

      const { data, error } = await supabase.functions.invoke('stripe-create-payment-link', {
        body: { 
          contact_id: deal.contact_id,
          deal_id: dealId,
          origin_context: 'deal_detail_setup',
          mode: 'setup',
          success_url: window.location.href,
          cancel_url: window.location.href
        }
      });
      
      if (error) throw error;
      
      if (data?.url) {
        setCardSetupLink(data.url);
        setShowCardSetupModal(true);
      }
    } catch (error) {
      console.error('Error creating setup link:', error);
      toast.error("Erro ao criar link: " + error.message);
    } finally {
      setIsCreatingSetupLink(false);
    }
  };

  const handleChargeClick = () => {
    setShowCardActionPopover(false);
    setShowChargeModal(true);
  };

  const handleGeneratePaymentLink = async (amount, description) => {
    setIsCreatingPaymentLink(true);
    
    try {
      if (!deal?.contact_id) {
        throw new Error("Contato não encontrado nesta negociação");
      }

      const { data, error } = await supabase.functions.invoke('stripe-create-payment-link', {
        body: { 
          contact_id: deal.contact_id,
          deal_id: dealId,
          amount: amount,
          description: description || deal?.name || 'Pagamento',
          origin_context: 'deal_detail_payment',
          mode: 'payment',
          success_url: window.location.href,
          cancel_url: window.location.href
        }
      });
      
      if (error) throw error;
      
      if (data?.url) {
        setPaymentLink(data.url);
        setChargeAmount(amount);
        setShowChargeModal(false);
        setShowPaymentLinkModal(true);
      }
    } catch (error) {
      console.error('Error creating payment link:', error);
      toast.error("Erro ao criar link: " + error.message);
    } finally {
      setIsCreatingPaymentLink(false);
    }
  };

  const [isSyncingStripe, setIsSyncingStripe] = useState(false);
  const [paymentLinkExpired, setPaymentLinkExpired] = useState(false);
  const [isRegeneratingLink, setIsRegeneratingLink] = useState(false);

  const handleSyncStripe = async () => {
    setIsSyncingStripe(true);
    try {
      const { data, error } = await supabase.functions.invoke('stripe-sync-payment-method', {
        body: { deal_id: dealId }
      });
      
      // Detectar se sessão expirou (retorna 200 com expired: true)
      if (data?.expired) {
        setPaymentLinkExpired(true);
        toast.warning("⏰ Link expirado! O cliente não preencheu os dados do cartão a tempo.");
        return;
      }
      
      if (error) throw error;
      
      if (data?.success) {
        setPaymentLinkExpired(false);
        toast.success("✅ Stripe sincronizado! Cartão salvo.");
        await loadDeal();
      } else if (data?.pending) {
        // Cliente ainda não completou o preenchimento do cartão
        toast.info("⏳ O cliente ainda não preencheu os dados do cartão. Aguarde e tente novamente.");
      } else {
        toast.error("❌ " + (data?.error || data?.message || "Erro desconhecido"));
      }
    } catch (error) {
      console.error('Stripe sync error:', error);
      toast.error("❌ Erro ao sincronizar Stripe: " + error.message);
    } finally {
      setIsSyncingStripe(false);
    }
  };

  const handleRegeneratePaymentLink = async () => {
    setIsRegeneratingLink(true);
    try {
      const { data, error } = await supabase.functions.invoke('stripe-create-checkout', {
        body: { deal_id: dealId }
      });
      
      if (error) throw error;
      
      setPaymentLinkExpired(false);
      toast.success("✅ Novo link de pagamento gerado!");
      await loadDeal();
    } catch (error) {
      console.error('Error regenerating payment link:', error);
      toast.error("❌ Erro ao regenerar link: " + error.message);
    } finally {
      setIsRegeneratingLink(false);
    }
  };

  // Permissão para excluir deal: apenas Admin e SalesManager
  const canDeleteDeal = !permissionsLoading && (isAdmin === true || isSalesManager === true);

  const handleDeleteDeal = async () => {
    setIsDeletingDeal(true);
    try {
      const { error } = await supabase
        .from('deals')
        .delete()
        .eq('id', dealId);
      
      if (error) throw error;
      
      toast.success(t('deals.dealDeleted') || 'Negociação excluída com sucesso');
      navigate(createPageUrl("Deals"));
    } catch (error) {
      console.error("Error deleting deal:", error);
      toast.error(t('deals.errorDeletingDeal') || 'Erro ao excluir negociação');
    } finally {
      setIsDeletingDeal(false);
      setShowDeleteDealDialog(false);
    }
  };

  if (dealLoading) {
    return <div className="bg-white p-6 md:p-8">
        <div className="max-w-6xl mx-auto space-y-8">
          <Skeleton className="h-10 w-64" />
          <Skeleton className="h-32 w-full" />
          <Skeleton className="h-96 w-full" />
        </div>
      </div>;
  }

  if (!deal) {
    return (
      <div className="bg-white p-6 md:p-8">
        <div className="max-w-6xl mx-auto">
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              {t('deals.dealNotFound')}
            </AlertDescription>
          </Alert>
          <Button 
            onClick={() => navigate(createPageUrl("Deals"))}
            className="mt-4"
          >
            {t('deals.backToDeals')}
          </Button>
        </div>
      </div>
    );
  }

  // Helper function to get all cases (new format + legacy)
  const getAllCases = () => {
    const allCases = [...(deal.cases || [])];

    // Include legacy single case if exists and is not already part of new `cases` array
    // Check for `case_type` as a simple indicator for legacy
    if (deal.case_type && !allCases.some(c => c.case_type === deal.case_type && c.case_price === deal.case_price)) {
      allCases.push({
        case_type: deal.case_type,
        case_pricing_type: deal.case_pricing_type,
        case_price: deal.case_price,
        case_quantity: deal.case_quantity
      });
    }
    return allCases;
  };

  // Helper function to get all services (new format + legacy)
  const getAllServices = () => {
    const allServices = [...(deal.services || [])];

    // Include legacy single service if exists and is not already part of new `services` array
    // Check for `service_type` as a simple indicator for legacy
    if (deal.service_type && !allServices.some(s => s.service_type === deal.service_type && s.service_price === deal.service_price)) {
      allServices.push({
        service_type: deal.service_type,
        service_pricing_type: deal.service_pricing_type,
        service_price: deal.service_price,
        service_quantity: deal.service_quantity
      });
    }
    return allServices;
  };
  const allCases = getAllCases();
  const allServices = getAllServices();

  // Calculate total from items
  const calculateItemsTotal = () => {
    return items.reduce((sum, item) => {
      const baseTotal = (item.unit_price || 0) * (item.quantity || 1);
      const dependentTotal = (item.dependent_price || 0) * (item.num_dependents || 0);
      const subtotal = baseTotal + dependentTotal;
      let discount = 0;
      if (item.discount_percent > 0) {
        discount = subtotal * (item.discount_percent / 100);
      } else {
        discount = item.discount_amount || 0;
      }
      return sum + (subtotal - discount);
    }, 0);
  };
  const totalFromItems = calculateItemsTotal();
  
  // Calculate final total with discount or markup
  const calculateFinalTotal = () => {
    const subtotal = totalFromItems;
    
    if (deal?.discount_type === 'percent' && deal?.discount_value > 0) {
      return subtotal - (subtotal * (deal.discount_value / 100));
    } else if (deal?.discount_type === 'amount' && deal?.discount_value > 0) {
      return Math.max(0, subtotal - deal.discount_value);
    } else if (deal?.discount_type === 'markup_percent' && deal?.discount_value > 0) {
      return subtotal + (subtotal * (deal.discount_value / 100));
    } else if (deal?.discount_type === 'markup_amount' && deal?.discount_value > 0) {
      return subtotal + deal.discount_value;
    }
    
    return subtotal;
  };

  const finalTotal = calculateFinalTotal();
  const hasAdjustment = deal?.discount_type !== 'none' && deal?.discount_value > 0 && totalFromItems > 0;
  const isMarkup = deal?.discount_type?.startsWith('markup_');
  const adjustmentAmount = hasAdjustment 
    ? (deal.discount_type === 'percent' || deal.discount_type === 'markup_percent'
        ? totalFromItems * (deal.discount_value / 100)
        : deal.discount_value)
    : 0;

  // Handle discount/markup save - now saves BOTH subtotal_amount and total_amount
  const handleSaveDiscount = async () => {
    try {
      setIsProcessing(true);
      
      const newFinalTotal = (() => {
        if (discountType === 'percent' && discountValue > 0) {
          return totalFromItems - (totalFromItems * (discountValue / 100));
        } else if (discountType === 'amount' && discountValue > 0) {
          return Math.max(0, totalFromItems - discountValue);
        } else if (discountType === 'markup_percent' && discountValue > 0) {
          return totalFromItems + (totalFromItems * (discountValue / 100));
        } else if (discountType === 'markup_amount' && discountValue > 0) {
          return totalFromItems + discountValue;
        }
        return totalFromItems;
      })();

      const { error } = await supabase
        .from('deals')
        .update({
          discount_type: discountType,
          discount_value: discountValue,
          subtotal_amount: totalFromItems,  // NEW: Always save subtotal
          total_amount: newFinalTotal
        })
        .eq('id', dealId);
      
      if (error) throw error;
      
      await loadDeal();
      setShowDiscountDialog(false);
      toast.success(discountType.startsWith('markup_') ? 'Acréscimo aplicado com sucesso!' : t('messages.discountApplied'));
    } catch (error) {
      console.error('Error saving adjustment:', error);
      toast.error(discountType.startsWith('markup_') ? 'Erro ao salvar acréscimo' : t('messages.errorSavingDiscount'));
    } finally {
      setIsProcessing(false);
    }
  };
  return <div className="bg-white dark:bg-slate-900 p-3 sm:p-4 md:p-8 overflow-x-hidden">
      <div className="max-w-6xl mx-auto">
        {/* Back Button */}
        <Button 
          onClick={goBack} 
          variant="ghost" 
          size="sm"
          className="mb-3 gap-2 text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="w-4 h-4" />
          {t('common.back')}
        </Button>
        
        {/* Compact Unified Header */}
        <Card className="mb-3 border-slate-200 dark:border-slate-700 bg-gradient-to-r from-slate-50 to-white dark:from-slate-800 dark:to-slate-900 shadow-sm">
          <CardContent className="py-2.5 px-3 sm:px-4 md:px-6">
            <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4">
              {/* Avatar + Contact + Deal Name */}
              <div className="flex items-center gap-3 flex-1 min-w-0">
                {contact ? (
                  <>
                    <div className="w-10 h-10 bg-gradient-to-br from-blue-600 to-blue-700 rounded-full flex items-center justify-center flex-shrink-0">
                      <span className="text-white font-bold text-base">
                        {contact.first_name?.charAt(0).toUpperCase() || contact.full_name?.charAt(0).toUpperCase() || 'C'}
                      </span>
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <button onClick={goToContactDetail} className="text-sm font-bold text-slate-900 dark:text-slate-100 hover:text-blue-600 hover:underline truncate">
                          {contact.first_name && contact.last_name ? `${contact.first_name} ${contact.last_name}` : contact.full_name}
                        </button>
                        <span className="text-muted-foreground text-xs">•</span>
                        <span className="text-sm font-medium text-slate-700 dark:text-slate-300 truncate">
                          {deal.name || items?.[0]?.catalog_item_name || t('deals.untitledDeal')}
                        </span>
                        <Badge
                          variant="outline" 
                          className={`${stageColors[deal.stage]?.bg || 'bg-slate-100'} ${stageColors[deal.stage]?.text || 'text-slate-700'} border-0 text-[10px] flex-shrink-0`}
                        >
                          {t(`dealStages.${deal.stage}`) || deal.stage}
                        </Badge>
                        {deal.deal_status === 'cancelled' && (
                          <Badge variant="destructive" className="text-[10px] flex-shrink-0">CANCELADO</Badge>
                        )}
                        {deal.converted_from_deal_id && (
                          <Badge className="bg-purple-100 text-purple-700 border-purple-200 text-[10px] flex-shrink-0">
                            <ArrowUpRight className="w-3 h-3 mr-0.5" />Upsell
                          </Badge>
                        )}
                        {dealId && !['won', 'lost'].includes(deal.stage) && (
                          <DealTagsDisplay dealId={dealId} compact maxTags={3} />
                        )}
                      </div>
                      <div className="flex items-center gap-3 mt-0.5 text-xs text-muted-foreground">
                        {contact.primary_phone && (
                          <button onClick={() => {
                            if (!isReady) { toast.info('Reconectando...'); return; }
                            if (!isInitiatingCall) {
                              makeCall(getFullPhoneE164(contact.primary_phone, contact.primary_phone_country_code), contact.id).catch(() => toast.error('Erro ao iniciar chamada'));
                            }
                          }} className="flex items-center gap-1 hover:text-blue-600 transition-colors">
                            <Phone className="w-3 h-3" />
                            <span>{formatPhoneNumber(contact.primary_phone, contact.primary_phone_country_code)}</span>
                          </button>
                        )}
                        {contact.primary_email && (
                          <a href={`mailto:${contact.primary_email}`} className="flex items-center gap-1 hover:text-blue-600 transition-colors hidden sm:flex">
                            <Mail className="w-3 h-3" />
                            <span className="truncate max-w-[160px]">{contact.primary_email}</span>
                          </a>
                        )}
                      </div>
                    </div>
                  </>
                ) : (
                  <div className="min-w-0 flex-1">
                    <span className="text-sm font-bold text-slate-900 dark:text-slate-100">
                      {deal.name || deal.deal_name || t('deals.untitledDeal')}
                    </span>
                  </div>
                )}
              </div>

              {/* Amount + Actions */}
              <div className="flex items-center gap-2 flex-shrink-0">
                <span className="text-base font-bold text-slate-900 dark:text-slate-100">
                  {new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format((finalTotal - (deal?.upsell_credit_amount || 0)) || 0)}
                </span>

                <Popover open={showCardActionPopover} onOpenChange={setShowCardActionPopover}>
                  <PopoverTrigger asChild>
                    <Button disabled={isLoadingCard || isCreatingSetupLink} variant="outline" size="sm" className="gap-1 h-8 px-2">
                      <CreditCard className="w-3.5 h-3.5" />
                      <ChevronDown className="w-3 h-3" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-48 p-2" align="end">
                    <div className="space-y-1">
                      <Button variant="ghost" className="w-full justify-start h-9 text-sm" onClick={handleChargeClick}>
                        <DollarSign className="w-4 h-4 mr-2" />Cobrar
                      </Button>
                      <Button variant="ghost" className="w-full justify-start h-9 text-sm" onClick={handleSaveCardClick} disabled={isCreatingSetupLink}>
                        <Wallet className="w-4 h-4 mr-2" />Salvar Cartão
                      </Button>
                    </div>
                  </PopoverContent>
                </Popover>

                {deal?.contact_id && (
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button variant="outline" size="sm" className="gap-1 h-8 px-2">
                        <Video className="w-3.5 h-3.5" />
                        <ChevronDown className="w-3 h-3" />
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-52 p-1" align="end">
                      <Button variant="ghost" className="w-full justify-start h-9 text-sm" onClick={() => setShowScheduleMeetingModal(true)}>
                        <Calendar className="w-4 h-4 mr-2" />Agendar
                      </Button>
                      <Button variant="ghost" className="w-full justify-start h-9 text-sm" disabled={dailyRing.isStarting} onClick={() => dailyRing.startDailyRing({ contactId: deal.contact_id, dealId })}>
                        <Users className="w-4 h-4 mr-2" />{dailyRing.isStarting ? 'Iniciando...' : 'Chamar Consultores'}
                      </Button>
                      <Button variant="ghost" className="w-full justify-start h-9 text-sm" disabled={dailyRing.isStarting} onClick={() => setShowDirectCallModal(true)}>
                        <UserPlus className="w-4 h-4 mr-2" />Chamar Pessoa
                      </Button>
                    </PopoverContent>
                  </Popover>
                )}

                {deal?.contact_id && (
                  <Button variant="outline" size="sm" className="h-8 px-2" onClick={() => setShowWhatsAppSheet(true)} title="Abrir WhatsApp">
                    <MessageCircle className="w-4 h-4 text-green-500" />
                  </Button>
                )}

                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="outline" size="sm" className="h-8 px-2">
                      <MoreVertical className="w-4 h-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem onClick={() => setShowStageDialog(true)} disabled={!isEditable}>
                      <TrendingUp className="w-4 h-4 mr-2" />{t('deals.changeStage')}
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => setShowProductsModal(true)} disabled={!isEditable}>
                      <Package className="w-4 h-4 mr-2" />{t('deals.editProducts')}
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => { setEditingTitle(deal.name || deal.deal_name || ''); setShowEditTitleDialog(true); }} disabled={!isEditable}>
                      <Pencil className="w-4 h-4 mr-2" />{t('deals.editTitle') || 'Editar Título'}
                    </DropdownMenuItem>
                    {(deal.stage === 'won' || deal.stage === 'WON') && deal.deal_status !== 'cancelled' && (
                      <>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem onClick={() => setShowCancelDealModal(true)} className="text-destructive focus:text-destructive">
                          <Ban className="w-4 h-4 mr-2" />Cancelar Contrato
                        </DropdownMenuItem>
                      </>
                    )}
                    {canDeleteDeal && (
                      <>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem onClick={() => setShowDeleteDealDialog(true)} className="text-destructive focus:text-destructive">
                          <Trash2 className="w-4 h-4 mr-2" />{t('deals.deleteDeal')}
                        </DropdownMenuItem>
                      </>
                    )}
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Alerts */}
        {isClosed && !canChangeStage && (
          <Alert className="mb-3 border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/30">
            <AlertCircle className="h-4 w-4 text-amber-600 dark:text-amber-400" />
            <AlertDescription className="text-amber-800 dark:text-amber-200">
              Este Deal está fechado ({deal.stage}). Apenas Admin ou Sales Manager podem alterar o stage.
            </AlertDescription>
          </Alert>
        )}

        {deal.deal_status === 'cancelled' && (
          <Alert className="mb-3 border-red-300 dark:border-red-800 bg-red-50 dark:bg-red-900/30">
            <Ban className="h-4 w-4 text-red-600 dark:text-red-400" />
            <AlertDescription className="flex items-center justify-between text-red-800 dark:text-red-200">
              <div>
                <strong>Contrato Cancelado</strong>
                {deal.cancelled_at && <span className="ml-2">em {safeFormat(new Date(deal.cancelled_at), "dd/MM/yyyy")}</span>}
                {deal.cancellation_reason && <span className="ml-2">— {deal.cancellation_reason}</span>}
              </div>
              <Button variant="outline" size="sm" onClick={() => setShowRevertCancelModal(true)} className="ml-4 border-red-300 text-red-700 hover:bg-red-100 dark:border-red-700 dark:text-red-300">
                <RotateCcw className="h-4 w-4 mr-2" />Reverter
              </Button>
            </AlertDescription>
          </Alert>
        )}

        {/* Fixed Stage Progress / Funnel */}
        <div className="mb-4">
          <DealStageProgress
            currentStage={deal.stage}
            onStageChange={handleUpdateStage}
            createdDate={deal.created_at}
            closedDate={deal.closed_date}
            expectedCloseDate={deal.expected_close_date}
            startDate={deal.start_date}
            canChangeStage={canChangeStage}
            onStartDateChange={isEditable ? async (date) => {
              try {
                await supabase.from('deals').update({ start_date: safeFormat(date, 'yyyy-MM-dd') }).eq('id', deal.id);
                await loadDeal();
                toast.success('Data de início atualizada');
              } catch (error) {
                console.error('Error updating start date:', error);
                toast.error('Error updating start date');
              }
            } : undefined}
            onClosingDateChange={isEditable ? async (date) => {
              const fieldToUpdate = deal.stage === 'WON' || deal.stage === 'LOST' ? 'closed_date' : 'expected_close_date';
              try {
                await supabase.from('deals').update({ [fieldToUpdate]: safeFormat(date, 'yyyy-MM-dd') }).eq('id', deal.id);
                await loadDeal();
                toast.success('Data de fechamento atualizada');
              } catch (error) {
                console.error('Error updating closing date:', error);
                toast.error('Error updating closing date');
              }
            } : undefined}
            onRequestLostReason={() => { setClosedDateForLost(new Date()); setShowLostReasonDialog(true); }}
            onRequestPostMeeting={() => setShowPostMeetingModal(true)}
            onRequestQualification={async () => {
              const validation = await validateDealForScheduled(deal.id);
              setQualificationMissingFields(validation.missingFields);
              setShowQualificationModal(true);
            }}
          />
        </div>

        {/* Tabs + Qualify button */}
        <div className="flex items-center justify-between gap-4 mb-6">
          <TabGroup tabs={[{
            id: 'details',
            label: t('tabs.details') || 'Detalhes'
          }, {
            id: 'payments',
            label: t('tabs.payments')
          }, {
            id: 'contracts',
            label: t('tabs.contracts')
          }, {
            id: 'activities',
            label: 'Atividades'
          }, {
            id: 'assignment_history',
            label: 'Atribuição'
          }]} activeTab={activeTab} onTabChange={setActiveTab} className="flex-1" />
          {isEditable && ['new', 'contact'].includes(deal.stage) && (
            <Button onClick={() => setShowFullQualificationModal(true)} size="sm" className="gap-2 shrink-0">
              <ClipboardCheck className="w-4 h-4" />
              {t('deals.qualify') || 'Qualificar'}
            </Button>
          )}
        </div>

        {activeTab === 'details' && <div className="space-y-6">
            {/* DealOverview metrics */}
            <DealOverview
              dealId={dealId} 
              dealName={deal.name || deal.deal_name} 
              stage={deal.stage} 
              totalAmount={deal.total_amount || totalFromItems} 
              contactName={contact ? `${contact.first_name || ''} ${contact.last_name || ''}`.trim() : 'Unknown'} 
              contactId={deal.contact_id}
            />

            {/* Deal Info Card */}
            <Card>
              <CardContent className="p-6">
                <h3 className="text-sm font-semibold text-slate-700 dark:text-slate-300 mb-4">{t('dealInfo.title')}</h3>
                <div className="space-y-4">
                  <div>
                    <p className="text-xs text-slate-500 dark:text-slate-400 mb-2">{t('dealInfo.dealOwner')}</p>
                    <div className="flex items-center gap-2">
                      <p className="text-slate-900 dark:text-slate-100 font-medium flex-1">
                        {assignedUser ? assignedUser.first_name && assignedUser.last_name ? `${assignedUser.first_name} ${assignedUser.last_name}` : assignedUser.full_name : t('dealInfo.unassigned')}
                      </p>
                      {isEditable && (
                        <button onClick={() => setShowSalesTeamDialog(true)} className="p-1.5 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition-colors" title="Editar equipe de vendas">
                          <UserCog className="w-4 h-4 text-blue-600" />
                        </button>
                      )}
                    </div>
                  </div>
                  <div>
                    <p className="text-xs text-slate-500 dark:text-slate-400">{t('dealInfo.salesDevelopment')}</p>
                    <p className="text-sm text-slate-900 dark:text-slate-100 mt-1">
                      {sdrUser ? sdrUser.first_name && sdrUser.last_name ? `${sdrUser.first_name} ${sdrUser.last_name}` : sdrUser.full_name : t('dealInfo.unassigned')}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-500 dark:text-slate-400">{t('dealInfo.createdAt')}</p>
                    <p className="text-sm text-slate-900 dark:text-slate-100 mt-1">
                      {deal.created_at ? safeFormat(new Date(deal.created_at), "MMM dd, yyyy 'at' h:mm a") : 'N/A'}
                    </p>
                  </div>
                  {creatorUser && <div>
                    <p className="text-xs text-slate-500 dark:text-slate-400">{t('dealInfo.createdBy')}</p>
                    <p className="text-sm text-slate-900 dark:text-slate-100 mt-1">{creatorUser.display_name || creatorUser.email}</p>
                  </div>}
                  {/* Business Unit */}
                  <div>
                    <p className="text-xs text-slate-500 dark:text-slate-400 mb-1">{t('dealInfo.businessUnit')}</p>
                    {isEditable ? (
                      <BusinessUnitSelector
                        value={deal.business_unit || ''}
                        onChange={async (value) => {
                          try {
                            await supabase.from('deals').update({ business_unit: value }).eq('id', deal.id);
                            await loadDeal();
                            toast.success(t('dealInfo.businessUnitUpdated'));
                          } catch (error) {
                            console.error('Error updating business unit:', error);
                            toast.error(t('dealInfo.errorUpdatingBusinessUnit'));
                          }
                        }}
                      />
                    ) : (
                      <p className="text-sm text-slate-900 dark:text-slate-100">{getBusinessUnitWithFlag(deal.business_unit)}</p>
                    )}
                  </div>
                  {/* Source & Channel */}
                  <div className="space-y-3">
                    <div>
                      <p className="text-xs text-slate-500 mb-1">{t('sourceChannel.dealSource')}</p>
                      {(isEditable && (!deal.deals_source || deal.deals_source === 'Unselected')) || isAdmin ? (
                        <Select
                          value={deal.deals_source || 'Unselected'}
                          onValueChange={async (value) => {
                            try {
                              await supabase.from('deals').update({ deals_source: value }).eq('id', deal.id);
                              await loadDeal();
                              toast.success(t('sourceChannel.sourceUpdated'));
                            } catch (error) {
                              toast.error(t('sourceChannel.errorUpdatingSource'));
                            }
                          }}
                        >
                          <SelectTrigger className="w-full h-8 text-sm"><SelectValue placeholder={t('sourceChannel.selectSource')} /></SelectTrigger>
                          <SelectContent className="bg-white z-50">
                            {SOURCE_TYPES.map((source) => (<SelectItem key={source.value} value={source.value}>{t(source.labelKey)}</SelectItem>))}
                          </SelectContent>
                        </Select>
                      ) : (
                        <div className="flex items-center gap-2">
                          <Badge variant="outline" className="capitalize">
                            {deal.deals_source ? t(SOURCE_TYPES.find(s => s.value === deal.deals_source)?.labelKey || 'sourceChannel.sources.unselected') : t('sourceChannel.sources.unselected')}
                          </Badge>
                          {deal.deals_source && deal.deals_source !== 'Unselected' && <span className="text-xs text-slate-400">🔒</span>}
                        </div>
                      )}
                    </div>
                    <div>
                      <p className="text-xs text-slate-500 mb-1">{t('sourceChannel.dealChannel')}</p>
                      {isEditable ? (
                        <Select
                          value={deal.deals_channel || 'Other'}
                          onValueChange={async (value) => {
                            try {
                              await supabase.from('deals').update({ deals_channel: value }).eq('id', deal.id);
                              await loadDeal();
                              toast.success(t('sourceChannel.channelUpdated'));
                            } catch (error) {
                              toast.error(t('sourceChannel.errorUpdatingChannel'));
                            }
                          }}
                        >
                          <SelectTrigger className="w-full h-8 text-sm"><SelectValue placeholder={t('sourceChannel.selectChannel')} /></SelectTrigger>
                          <SelectContent className="bg-white z-50">
                            {CHANNEL_TYPES.map((channel) => (<SelectItem key={channel.value} value={channel.value}>{t(channel.labelKey)}</SelectItem>))}
                          </SelectContent>
                        </Select>
                      ) : (
                        <Badge variant="outline" className="capitalize">
                          {deal.deals_channel ? t(CHANNEL_TYPES.find(c => c.value === deal.deals_channel)?.labelKey || 'sourceChannel.channels.other') : t('sourceChannel.channels.other')}
                        </Badge>
                      )}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Notes Section */}
            <NotesSection 
              notes={notes} 
              notesLoading={notesLoading} 
              createNote={createNote} 
              deleteNote={deleteNote}
              isEditable={isEditable}
            />


            {/* Items List */}
            {hasActiveContract && (
              <div className="flex items-center gap-2 p-3 rounded-lg bg-amber-50 border border-amber-200 text-amber-800 dark:bg-amber-900/20 dark:border-amber-700 dark:text-amber-200">
                <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5 shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>
                <span className="text-sm font-medium">Edição bloqueada: existe um contrato ativo. Cancele o contrato na aba Contratos para editar.</span>
              </div>
            )}
            {deal?.converted_from_deal_id && (
              <Card className="border-purple-200 bg-gradient-to-br from-purple-50 to-indigo-50 dark:from-purple-900/20 dark:to-indigo-900/20">
                <CardContent className="p-4">
                  <div className="flex items-center gap-2 mb-3">
                    <ArrowUpRight className="w-5 h-5 text-purple-600" />
                    <h3 className="font-semibold text-purple-900 dark:text-purple-100">{t('deals.upsellConversion') || 'Conversão de Upsell'}</h3>
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                    <div>
                      <p className="text-slate-500 dark:text-slate-400">{t('deals.newContractValue') || 'Valor do Contrato Novo'}</p>
                      <p className="font-bold text-lg">{formatCurrency(finalTotal)}</p>
                    </div>
                    <div>
                      <p className="text-slate-500 dark:text-slate-400">{t('deals.previousDealCredit') || 'Crédito do Deal Anterior'}</p>
                      <p className="font-bold text-lg text-purple-600">-{formatCurrency(deal.upsell_credit_amount || 0)}</p>
                    </div>
                    <div>
                      <p className="text-slate-500 dark:text-slate-400">{t('deals.netSaleValue') || 'Valor da Venda (Comissão)'}</p>
                      <p className="font-bold text-lg text-emerald-600">{formatCurrency(finalTotal - (deal.upsell_credit_amount || 0))}</p>
                    </div>
                    <div>
                      <p className="text-slate-500 dark:text-slate-400">{t('deals.transferredPayments') || 'Pagamentos Transferidos'}</p>
                      <p className="font-bold text-lg text-blue-600">{formatCurrency(upsellCreditsTotal)}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}
            <Card>
              <CardContent className="p-6">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-lg font-bold text-slate-900">{t('dealItems.title')}</h2>
                  {isDetailsEditable && (
                    <Button onClick={() => setShowAddItemDialog(true)} size="icon" variant="ghost">
                      <Plus className="w-5 h-5" />
                    </Button>
                  )}
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-4 mb-4 md:mb-6">
                  {items && items.length > 0 ? <>
                      {items.map(item => {
                  const catalogItem = item.catalog || catalogItems.find(c => c.id === item.catalog_id);
                  const baseTotal = (item.unit_price || 0) * (item.quantity || 1);
                  const dependentTotal = (item.dependent_price || 0) * (item.num_dependents || 0);
                  const subtotal = baseTotal + dependentTotal;
                  let discount = 0;
                  if (item.discount_percent > 0) {
                    discount = subtotal * (item.discount_percent / 100);
                  } else {
                    discount = item.discount_amount || 0;
                  }
                  const itemTotal = subtotal - discount;
                  return <div key={item.id} className="space-y-2">
                    <div className="p-3 md:p-4 bg-blue-50 rounded-lg border border-blue-200 group relative overflow-hidden cursor-pointer"
                      onClick={() => setExpandedItemId(expandedItemId === item.id ? null : item.id)}
                    >
                    {isDetailsEditable && (
                      <div className="absolute top-2 right-2 flex gap-1">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button size="sm" variant="outline" className="h-6 w-6 md:h-7 md:w-7 p-0 bg-white/90 backdrop-blur-sm border-slate-200 hover:bg-slate-100" onClick={(e) => e.stopPropagation()}>
                              <MoreVertical className="w-3 h-3 md:w-4 md:h-4 text-slate-600" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end" onClick={(e) => e.stopPropagation()}>
                            <DropdownMenuItem onClick={(e) => { e.stopPropagation(); setExpandedItemId(item.id); setEditingItemId(item.id); }}>
                              <Pencil className="w-4 h-4 mr-2" />{t('common.edit')}
                            </DropdownMenuItem>
                            <DropdownMenuItem className="text-destructive focus:text-destructive" onClick={async (e) => {
                              e.stopPropagation();
                              if (confirm(t('dealItems.confirmRemove'))) {
                                try {
                                  setIsProcessing(true);
                                  await deleteItem(item.id);
                                  const remainingItems = items.filter(i => i.id !== item.id);
                                  if (remainingItems.length === 0) {
                                    await supabase.from('deals').update({ discount_type: 'none', discount_value: 0, subtotal_amount: 0, total_amount: 0 }).eq('id', dealId);
                                  } else {
                                    const newSubtotal = remainingItems.reduce((sum, i) => {
                                      const cat = catalogItems.find(c => c.id === i.catalog_id);
                                      const bt = (i.quantity || 1) * (i.unit_price || cat?.base_price || 0);
                                      const depC = i.num_dependents || 0;
                                      const depP = cat?.dependent_price || 0;
                                      const dt = depC * depP;
                                      const is = bt + dt;
                                      const d = i.discount_percent > 0 ? is * (i.discount_percent / 100) : (i.discount_amount || 0);
                                      return sum + (is - d);
                                    }, 0);
                                    let newTotal = newSubtotal;
                                    if (deal?.discount_type === 'percent' && deal?.discount_value > 0) newTotal = newSubtotal * (1 - deal.discount_value / 100);
                                    else if (deal?.discount_type === 'amount' && deal?.discount_value > 0) newTotal = Math.max(0, newSubtotal - deal.discount_value);
                                    else if (deal?.discount_type === 'markup_percent' && deal?.discount_value > 0) newTotal = newSubtotal * (1 + deal.discount_value / 100);
                                    else if (deal?.discount_type === 'markup_amount' && deal?.discount_value > 0) newTotal = newSubtotal + deal.discount_value;
                                    await supabase.from('deals').update({ subtotal_amount: newSubtotal, total_amount: newTotal }).eq('id', dealId);
                                  }
                                  await loadDeal();
                                  toast.success(t('messages.itemRemoved'));
                                } catch (error) {
                                  console.error('Error removing item:', error);
                                  toast.error(t('messages.errorRemovingItem'));
                                } finally { setIsProcessing(false); }
                              }
                            }}>
                              <Trash2 className="w-4 h-4 mr-2" />{t('common.remove')}
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                    )}
                            <div className="flex items-center justify-between gap-2 pr-8">
                              <div className="min-w-0 flex-1">
                                <p className="font-medium text-sm md:text-base text-slate-900 truncate">{catalogItem?.name}</p>
                                {catalogItem?.pricing_type === 'per_unit' && item.quantity > 1 && <p className="text-xs text-slate-500 mt-1">{item.quantity} × {formatCurrency(item.unit_price || 0)}</p>}
                                <ItemPeoplePreview itemId={item.id} />
                              </div>
                              <span className="font-semibold text-sm md:text-base text-slate-900 flex-shrink-0">{formatCurrency(itemTotal)}</span>
                            </div>
                          </div>
                    {expandedItemId === item.id && (
                      <div className="mt-2" id={`item-card-${item.id}`}>
                        <Tabs value={itemSubtab[item.id] || 'people'} onValueChange={(v) => setItemSubtab(prev => ({ ...prev, [item.id]: v }))}>
                          <TabsList className="grid w-full grid-cols-2">
                            <TabsTrigger value="people">Pessoas</TabsTrigger>
                            <TabsTrigger value="eligibility">Elegibilidade</TabsTrigger>
                          </TabsList>
                          <TabsContent value="people" className="mt-3">
                            <ItemPeopleEditor
                              itemId={item.id}
                              catalogItemId={item.catalog_id}
                              dealContact={contact}
                              isEditable={isDetailsEditable}
                              isEditing={editingItemId === item.id}
                              onStartEdit={() => setEditingItemId(item.id)}
                              onFinishEdit={() => setEditingItemId(null)}
                            />
                          </TabsContent>
                          <TabsContent value="eligibility" className="mt-3">
                            <ItemEligibilityForm 
                              itemId={item.id} 
                              catalogId={item.catalog_id} 
                              currentEligibility={item.eligibility}
                              onSave={() => refetchDeal()} 
                            />
                          </TabsContent>
                        </Tabs>
                      </div>
                    )}
                  </div>;
                })}
                    </> : <div className="col-span-full text-center py-8 text-muted-foreground">
                      <Package className="w-8 h-8 mx-auto mb-2 opacity-50" />
                      <p className="text-sm">{t('dealItems.noItems')}</p>
                      {isDetailsEditable && <Button variant="outline" size="sm" className="mt-3" onClick={() => setShowAddItemDialog(true)}>
                        <Plus className="w-4 h-4 mr-2" />{t('dealItems.addItem')}
                      </Button>}
                    </div>}
                </div>
                {/* Totals */}
                {items && items.length > 0 && <div className="border-t pt-4 space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-slate-600">Subtotal</span>
                    <span className="text-sm font-semibold">{formatCurrency(totalFromItems)}</span>
                  </div>
                  {hasAdjustment && (
                    <div className="flex items-center justify-between">
                      <span className={`text-sm ${isMarkup ? 'text-emerald-600' : 'text-orange-600'}`}>
                        {isMarkup ? 'Acréscimo' : 'Desconto'} ({deal.discount_type === 'percent' || deal.discount_type === 'markup_percent' ? `${deal.discount_value}%` : formatCurrency(deal.discount_value)})
                      </span>
                      <span className={`text-sm font-semibold ${isMarkup ? 'text-emerald-600' : 'text-orange-600'}`}>
                        {isMarkup ? '+' : '-'}{formatCurrency(adjustmentAmount)}
                      </span>
                    </div>
                  )}
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span className="text-base font-bold text-slate-900">Total</span>
                      {isDetailsEditable && (
                        <Button variant="ghost" size="icon" onClick={() => {
                          if (deal) {
                            setDiscountType(deal.discount_type || 'none');
                            const currentValue = deal.discount_value || 0;
                            setDiscountValue(currentValue);
                            if (deal.discount_type === 'amount' || deal.discount_type === 'markup_amount') {
                              setDiscountValueDisplay(currentValue > 0 ? currentValue.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '');
                            } else {
                              setDiscountValueDisplay(currentValue > 0 ? currentValue.toString() : '');
                            }
                          }
                          setShowDiscountDialog(true);
                        }} className="h-7 w-7 md:h-8 md:w-8 flex-shrink-0" title="Ajustar valor">
                          <Edit2 className="w-3 h-3 md:w-4 md:h-4 text-emerald-600" />
                        </Button>
                      )}
                    </div>
                    <span className="text-base font-bold text-emerald-700">{formatCurrency(finalTotal)}</span>
                  </div>
                </div>}
              </CardContent>
            </Card>

            {/* Payment Plan */}
            <Card>
              <CardContent className="p-6">
                <h2 className="text-lg font-bold text-slate-900 mb-6">{t('deals.paymentPlan')}</h2>
                <PaymentPlanV2 
                  totalValue={finalTotal}
                  upsellCredits={upsellCreditsTotal}
                  dealId={dealId} 
                  onScheduleChange={setPaymentSchedule}
                  onPaymentsSaved={async () => {
                    await loadDeal();
                    toast.success('Plano de pagamento salvo! Veja a aba Pagamentos');
                  }}
                  disabled={!isDetailsEditable}
                  initialConfig={deal?.metadata?.payment_plan_config}
                  onConfigChange={async (config) => {
                    if (!dealId) return;
                    try {
                      const { error } = await supabase
                        .from('deals')
                        .update({ metadata: { ...deal?.metadata, payment_plan_config: config } })
                        .eq('id', dealId);
                      if (error) throw error;
                      await loadDeal();
                    } catch (error) {
                      console.error('Error saving payment plan config:', error);
                      throw error;
                    }
                  }}
                />
              </CardContent>
            </Card>

            {deal.description && <Card>
                <CardContent className="p-6">
                  <p className="text-sm font-semibold text-slate-700 mb-2">{t('common.description')}</p>
                  <p className="text-slate-600">{deal.description}</p>
                </CardContent>
              </Card>}
          </div>}

        {activeTab === 'payments' && <PaymentsTab dealId={dealId} dealTotal={finalTotal} upsellCredits={upsellCreditsTotal} isEditable={isEditable} paymentPlanConfig={deal?.metadata?.payment_plan_config} contactId={contact?.id} />}

        {activeTab === 'contracts' && <ContractsTab dealId={dealId} contact={deal?.contact || contact} deal={deal} finalTotal={finalTotal} onContractChange={refreshContractStatus} />}

        {activeTab === 'activities' && <DealActivityTimeline dealId={dealId} contactId={contact?.id} contactPhone={contact ? getFullPhoneE164(contact?.primary_phone, contact?.primary_phone_country_code) : undefined} contactName={contact ? `${contact.first_name || ''} ${contact.last_name || ''}`.trim() : undefined} dealName={deal?.name} isEditable={isEditable} />}

        {activeTab === 'assignment_history' && <AssignmentHistoryTab dealId={dealId} />}


        {/* Add/Edit Item Modal */}
        <AddItemModal
          isOpen={showAddItemDialog || !!editingItemId}
          onClose={() => {
            setShowAddItemDialog(false);
            setEditingItemId(null);
          }}
          catalogItems={catalogItems}
          dealId={dealId}
          dealContact={contact}
          editingItemId={editingItemId}
          onItemAdded={() => {
            loadDeal();
            loadItems();
          }}
          onItemUpdated={() => {
            loadDeal();
            loadItems();
            setEditingItemId(null);
            setExpandedItemId(null);
          }}
        />

        {/* Sales Team Dialog */}
        <Dialog open={showSalesTeamDialog} onOpenChange={setShowSalesTeamDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{t('deals.editSalesTeam')}</DialogTitle>
              <DialogDescription>
                {t('deals.assignSalesTeam')}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              {/* Sales Consultant */}
              <div className="space-y-2">
                <Label>{t('fields.salesConsultant')}</Label>
                <Select 
                  value={deal.sales_consultant || "unassigned"} 
                  onValueChange={async (value) => {
                    setIsProcessing(true);
                    try {
                      const newValue = value === "unassigned" ? null : value;
                      const oldValue = deal.sales_consultant || null;
                      await supabase.from('deals').update({
                        sales_consultant: newValue
                      }).eq('id', deal.id);
                      // Log assignment change
                      if (oldValue !== newValue) {
                        await supabase.from('deal_assignment_log').insert({
                          deal_id: deal.id,
                          from_user_id: oldValue,
                          to_user_id: newValue,
                          target_field: 'sales_consultant',
                          reason: 'manual',
                          changed_by: user?.id || null,
                        });
                      }
                      queryClient.invalidateQueries({ queryKey: ['deal-assignment-log', deal.id] });
                      await loadDeal();
                      toast.success(t('messages.salesConsultantUpdated'));
                    } catch (error) {
                      console.error("Error updating sales consultant:", error);
                      toast.error(t('messages.errorUpdating'));
                    }
                    setIsProcessing(false);
                  }}
                  disabled={isProcessing || isLoadingSalesUsers}
                >
                  <SelectTrigger className="border-slate-300">
                    <SelectValue placeholder={t('deals.selectConsultant')} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="unassigned">{t('common.unassigned')}</SelectItem>
                    {salesUsers
                      .filter(u => u.system_role?.code === 'sales_consultant' || u.system_role?.code === 'sales_manager')
                      .map(u => (
                        <SelectItem key={u.id} value={u.id}>
                          {u.display_name || `${u.first_name || ''} ${u.last_name || ''}`.trim() || u.email}
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Sales Development */}
              <div className="space-y-2">
                <Label>{t('fields.salesDevelopment')}</Label>
                <Select 
                  value={deal.sales_development || "unassigned"} 
                  onValueChange={async (value) => {
                    setIsProcessing(true);
                    try {
                      const newValue = value === "unassigned" ? null : value;
                      const oldValue = deal.sales_development || null;
                      await supabase.from('deals').update({
                        sales_development: newValue
                      }).eq('id', deal.id);
                      // Log assignment change
                      if (oldValue !== newValue) {
                        await supabase.from('deal_assignment_log').insert({
                          deal_id: deal.id,
                          from_user_id: oldValue,
                          to_user_id: newValue,
                          target_field: 'sales_development',
                          reason: 'manual',
                          changed_by: user?.id || null,
                        });
                      }
                      queryClient.invalidateQueries({ queryKey: ['deal-assignment-log', deal.id] });
                      await loadDeal();
                      toast.success(t('messages.salesDevelopmentUpdated'));
                    } catch (error) {
                      console.error("Error updating sales development:", error);
                      toast.error(t('messages.errorUpdating'));
                    }
                    setIsProcessing(false);
                  }}
                  disabled={isProcessing || isLoadingSalesUsers}
                >
                  <SelectTrigger className="border-slate-300">
                    <SelectValue placeholder={t('deals.selectSdr')} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="unassigned">{t('common.unassigned')}</SelectItem>
                    {salesUsers
                      .filter(u => u.system_role?.code === 'sales_dev')
                      .map(u => (
                        <SelectItem key={u.id} value={u.id}>
                          {u.display_name || `${u.first_name || ''} ${u.last_name || ''}`.trim() || u.email}
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="flex justify-end">
              <Button onClick={() => setShowSalesTeamDialog(false)}>
                {t('common.done')}
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Assignment Dialog (deprecated - kept for compatibility) */}
        <Dialog open={showAssignDialog} onOpenChange={setShowAssignDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{t('deals.assignDealOwner')}</DialogTitle>
              <DialogDescription>
                {t('deals.selectUser')}
              </DialogDescription>
            </DialogHeader>
            <div className="py-4">
              <Select value={deal.sales_consultant || "unassigned"} onValueChange={async value => {
              setIsProcessing(true);
              try {
                await supabase.from('deals').update({
                  sales_consultant: value === "unassigned" ? null : value
                }).eq('id', deal.id);
                await loadDeal();
                setShowAssignDialog(false);
                toast.success(t('messages.dealOwnerAssigned'));
              } catch (error) {
                console.error("Error updating assigned user:", error);
                toast.error(t('messages.errorUpdating'));
              }
              setIsProcessing(false);
            }} disabled={isProcessing}>
                <SelectTrigger className="border-slate-300">
                  <SelectValue placeholder={t('deals.selectUser')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="unassigned">{t('common.unassigned')}</SelectItem>
                  {[...users]
                    .sort((a, b) => {
                      const nameA = (a.first_name && a.last_name ? `${a.first_name} ${a.last_name}` : a.display_name || a.email || '').toLowerCase();
                      const nameB = (b.first_name && b.last_name ? `${b.first_name} ${b.last_name}` : b.display_name || b.email || '').toLowerCase();
                      return nameA.localeCompare(nameB);
                    })
                    .map(u => <SelectItem key={u.id} value={u.id}>
                      {u.first_name && u.last_name ? `${u.first_name} ${u.last_name}` : u.display_name || u.email}
                    </SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </DialogContent>
        </Dialog>

        {/* Stage Dialog */}
        <Dialog open={showStageDialog} onOpenChange={setShowStageDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{t('deals.changeDealStage')}</DialogTitle>
              <DialogDescription>
                {t('deals.updateStage')}
              </DialogDescription>
            </DialogHeader>
            <div className="py-4">
              <Select value={deal.stage} onValueChange={handleUpdateStage} disabled={isProcessing}>
                <SelectTrigger className="border-slate-300">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="NEW">{t('stages.new')}</SelectItem>
                  <SelectItem value="PROSPECTING">{t('stages.prospecting')}</SelectItem>
                  <SelectItem value="QUALIFYING">{t('stages.qualifying')}</SelectItem>
                  <SelectItem value="PROPOSAL">{t('stages.proposal')}</SelectItem>
                  <SelectItem value="NEGOTIATING">{t('stages.negotiating')}</SelectItem>
                  <SelectItem value="WON">{t('stages.won')}</SelectItem>
                  <SelectItem value="LOST">{t('stages.lost')}</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </DialogContent>
        </Dialog>

        {/* Lost Reason Dialog */}
        <AlertDialog open={showLostReasonDialog} onOpenChange={setShowLostReasonDialog}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>{t('deals.markAsLost')}</AlertDialogTitle>
              <AlertDialogDescription>
                {t('deals.selectReason')}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <div className="py-4 space-y-4">
              <div>
                <Label htmlFor="lost-reason">{t('fields.reason')} *</Label>
                <Select value={lostReason} onValueChange={setLostReason}>
                  <SelectTrigger id="lost-reason" className="border-slate-300 mt-2">
                    <SelectValue placeholder={t('deals.selectReason')} />
                  </SelectTrigger>
                  <SelectContent>
                    {lostReasons.map(reason => (
                      <SelectItem key={reason.value} value={reason.value}>
                        {reason.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>{t('fields.closedDate')} *</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" className="w-full justify-start text-left font-normal mt-2">
                      <Calendar className="mr-2 h-4 w-4" />
                      {safeFormat(closedDateForLost, 'MMM d, yyyy')}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <CalendarUI
                      mode="single"
                      selected={closedDateForLost}
                      onSelect={(date) => date && setClosedDateForLost(date)}
                      initialFocus
                      className="p-3 pointer-events-auto"
                    />
                  </PopoverContent>
                </Popover>
              </div>
              
              <div>
                <Label htmlFor="verification-status">{t('deals.contactVerificationStatus')}</Label>
                <Select value={contactVerificationStatus} onValueChange={setContactVerificationStatus}>
                  <SelectTrigger id="verification-status" className="border-slate-300 mt-2">
                    <SelectValue placeholder={t('deals.keepUnchanged')} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="keep">{t('deals.keepUnchanged')}</SelectItem>
                    <SelectItem value="unverified">{t('verification.unverified')}</SelectItem>
                    <SelectItem value="verified">{t('verification.verified')}</SelectItem>
                    <SelectItem value="invalid">{t('verification.invalid')}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <AlertDialogFooter>
              <AlertDialogCancel onClick={() => {
                setShowLostReasonDialog(false);
                setLostReason('');
                setContactVerificationStatus('keep');
                setPendingStage(null);
              }}>
                {t('common.cancel')}
              </AlertDialogCancel>
              <AlertDialogAction onClick={handleConfirmLost} disabled={isProcessing || !lostReason}>
                {t('common.confirm')}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* WON Confirmation Dialog */}
        <AlertDialog open={showWonDialog} onOpenChange={setShowWonDialog}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>{t('deals.markAsWon')}</AlertDialogTitle>
              <AlertDialogDescription>
                {t('deals.confirmClosingDate')}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <div className="py-4 space-y-4">
              <div>
                <Label>{t('fields.closedDate')} *</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" className="w-full justify-start text-left font-normal mt-2">
                      <Calendar className="mr-2 h-4 w-4" />
                      {safeFormat(closedDateForWon, 'MMM d, yyyy')}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <CalendarUI
                      mode="single"
                      selected={closedDateForWon}
                      onSelect={(date) => date && setClosedDateForWon(date)}
                      initialFocus
                      className="p-3 pointer-events-auto"
                    />
                  </PopoverContent>
                </Popover>
              </div>
            </div>
            <AlertDialogFooter>
              <AlertDialogCancel onClick={() => {
                setShowWonDialog(false);
                setPendingStage(null);
              }}>
                Cancel
              </AlertDialogCancel>
              <AlertDialogAction onClick={handleConfirmWon} disabled={isProcessing}>
                Confirm
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Legacy Product Blocking Modal - Cannot be closed without adding product */}
        <AlertDialog open={showLegacyProductModal}>
          <AlertDialogContent 
            className="sm:max-w-md"
            onEscapeKeyDown={(e) => e.preventDefault()}
            onPointerDownOutside={(e) => e.preventDefault()}
            onInteractOutside={(e) => e.preventDefault()}
          >
            <AlertDialogHeader>
              <AlertDialogTitle className="flex items-center gap-2 text-amber-600">
                <AlertCircle className="h-5 w-5" />
                Ação Necessária
              </AlertDialogTitle>
              <AlertDialogDescription className="text-base">
                Para continuar trabalhando neste Deal em <strong>{deal?.stage}</strong>, você precisa adicionar o processo/serviço que está sendo negociado com este cliente.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter className="mt-4">
              <AlertDialogAction 
                onClick={() => setShowAddItemDialog(true)}
                className="w-full"
              >
                <Plus className="w-4 h-4 mr-2" />
                Adicionar Processo/Serviço
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Products Edit Modal */}
        <ProductsEditModal isOpen={showProductsModal} onClose={() => setShowProductsModal(false)} deal={deal} onSave={handleUpdateProducts} isProcessing={isProcessing} moduleSettings={moduleSettings} />

        {/* Discount/Markup Dialog */}
        <Dialog open={showDiscountDialog} onOpenChange={setShowDiscountDialog}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>Ajustar Valor do Deal</DialogTitle>
              <DialogDescription>
                Aplique desconto ou acréscimo sobre o valor total de todos os Cases & Services
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4 py-4">
              {/* Tipo de Ajuste */}
              <div className="space-y-2">
                <Label>Tipo de Ajuste</Label>
                <Select value={discountType} onValueChange={(value) => {
                  setDiscountType(value);
                  setDiscountValue(0);
                  setDiscountValueDisplay('');
                }}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Sem Ajuste</SelectItem>
                    <SelectItem value="percent">Desconto Percentual (%)</SelectItem>
                    <SelectItem value="amount">Desconto Valor Fixo ($)</SelectItem>
                    <SelectItem value="markup_percent">Acréscimo Percentual (%)</SelectItem>
                    <SelectItem value="markup_amount">Acréscimo Valor Fixo ($)</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Valor do Ajuste */}
              {discountType !== 'none' && (
                <div className="space-y-2">
                  <Label>
                    Valor {discountType.includes('percent') ? '(%)' : '($)'}
                  </Label>
                  <Input
                    type="text"
                    inputMode={discountType.includes('amount') ? 'decimal' : 'numeric'}
                    value={discountValueDisplay}
                    onChange={(e) => {
                      const inputValue = e.target.value;
                      
                      if (discountType.includes('percent')) {
                        const cleanValue = inputValue.replace(/[^\d.]/g, '');
                        const numValue = parseFloat(cleanValue) || 0;
                        
                        if (numValue <= 100) {
                          setDiscountValueDisplay(cleanValue);
                          setDiscountValue(numValue);
                        }
                      } else if (discountType.includes('amount')) {
                        const formatted = formatCurrencyInput(inputValue);
                        const numValue = parseCurrencyInput(inputValue);
                        
                        // For discounts, limit to subtotal. For markup, no limit
                        if (discountType === 'amount' && numValue > totalFromItems) {
                          return;
                        }
                        setDiscountValueDisplay(formatted);
                        setDiscountValue(numValue);
                      }
                    }}
                    placeholder={discountType.includes('percent') ? 'Ex: 10' : 'Ex: 1,212.00'}
                    className="text-lg"
                  />
                </div>
              )}

              {/* Preview for Discount */}
              {(discountType === 'percent' || discountType === 'amount') && discountValue > 0 && (
                <Card className="bg-orange-50 border-orange-200">
                  <CardContent className="p-4 space-y-2">
                    <div className="flex justify-between text-sm">
                      <span>Subtotal:</span>
                      <span className="font-semibold">{formatCurrency(totalFromItems)}</span>
                    </div>
                    <div className="flex justify-between text-sm text-orange-700">
                      <span>Desconto:</span>
                      <span className="font-semibold">
                        -{formatCurrency(
                          discountType === 'percent' 
                            ? totalFromItems * (discountValue / 100)
                            : discountValue
                        )}
                      </span>
                    </div>
                    <div className="flex justify-between text-base font-bold text-emerald-700 pt-2 border-t">
                      <span>Total Final:</span>
                      <span>
                        {formatCurrency(
                          discountType === 'percent'
                            ? totalFromItems - (totalFromItems * (discountValue / 100))
                            : Math.max(0, totalFromItems - discountValue)
                        )}
                      </span>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Preview for Markup */}
              {(discountType === 'markup_percent' || discountType === 'markup_amount') && discountValue > 0 && (
                <Card className="bg-emerald-50 border-emerald-200">
                  <CardContent className="p-4 space-y-2">
                    <div className="flex justify-between text-sm">
                      <span>Subtotal:</span>
                      <span className="font-semibold">{formatCurrency(totalFromItems)}</span>
                    </div>
                    <div className="flex justify-between text-sm text-emerald-700">
                      <span>Acréscimo:</span>
                      <span className="font-semibold">
                        +{formatCurrency(
                          discountType === 'markup_percent' 
                            ? totalFromItems * (discountValue / 100)
                            : discountValue
                        )}
                      </span>
                    </div>
                    <div className="flex justify-between text-base font-bold text-emerald-700 pt-2 border-t">
                      <span>Total Final:</span>
                      <span>
                        {formatCurrency(
                          discountType === 'markup_percent'
                            ? totalFromItems + (totalFromItems * (discountValue / 100))
                            : totalFromItems + discountValue
                        )}
                      </span>
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>

            <div className="flex gap-2">
              <Button
                variant="outline"
                onClick={() => setShowDiscountDialog(false)}
                className="flex-1"
              >
                Cancelar
              </Button>
              <Button
                onClick={async () => {
                  try {
                    setIsProcessing(true);
                    
                    const newFinalTotal = (() => {
                      if (discountType === 'percent' && discountValue > 0) {
                        return totalFromItems - (totalFromItems * (discountValue / 100));
                      } else if (discountType === 'amount' && discountValue > 0) {
                        return Math.max(0, totalFromItems - discountValue);
                      } else if (discountType === 'markup_percent' && discountValue > 0) {
                        return totalFromItems + (totalFromItems * (discountValue / 100));
                      } else if (discountType === 'markup_amount' && discountValue > 0) {
                        return totalFromItems + discountValue;
                      }
                      return totalFromItems;
                    })();

                    const { error } = await supabase
                      .from('deals')
                      .update({
                        discount_type: discountType,
                        discount_value: discountValue,
                        total_amount: newFinalTotal
                      })
                      .eq('id', dealId);
                    
                    if (error) throw error;
                    
                    await loadDeal();
                    setShowDiscountDialog(false);
                    toast.success(discountType.startsWith('markup_') ? 'Acréscimo aplicado com sucesso!' : 'Desconto aplicado com sucesso!');
                  } catch (error) {
                    console.error('Error saving adjustment:', error);
                    toast.error(discountType.startsWith('markup_') ? 'Erro ao salvar acréscimo' : 'Erro ao salvar desconto');
                  } finally {
                    setIsProcessing(false);
                  }
                }}
                disabled={isProcessing}
                className="flex-1"
              >
                {isProcessing ? 'Salvando...' : (discountType.startsWith('markup_') ? 'Aplicar Acréscimo' : 'Aplicar Desconto')}
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Delete Deal Dialog */}
        <AlertDialog open={showDeleteDealDialog} onOpenChange={setShowDeleteDealDialog}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>{t('deals.deleteDeal') || 'Excluir Negociação'}</AlertDialogTitle>
              <AlertDialogDescription>
                {t('deals.deleteConfirmation') || 'Tem certeza que deseja excluir esta negociação?'}
                
                <div className="mt-4 p-4 bg-amber-50 border border-amber-200 rounded-lg">
                  <p className="text-amber-800 text-sm">
                    <strong>{t('common.warning') || 'Atenção'}:</strong> {t('deals.deleteWarning') || 'Esta ação não pode ser desfeita. Todos os dados relacionados serão perdidos.'}
                  </p>
                </div>
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel disabled={isDeletingDeal}>{t('common.cancel')}</AlertDialogCancel>
              <AlertDialogAction 
                onClick={handleDeleteDeal}
                disabled={isDeletingDeal}
                className="bg-destructive hover:bg-destructive/90"
              >
                {isDeletingDeal ? (t('common.deleting') || 'Excluindo...') : t('common.delete')}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Schedule Meeting Modal */}
        {deal?.contact_id && (
          <ScheduleMeetingModal
            open={showScheduleMeetingModal}
            onClose={() => setShowScheduleMeetingModal(false)}
            dealId={dealId}
            contactId={deal.contact_id}
            dealName={deal?.name || null}
            contactName={contact ? `${contact.first_name ?? ''} ${contact.last_name ?? ''}`.trim() : null}
            contactEmail={contact?.email ?? null}
            defaultConsultantId={deal?.sales_consultant ?? null}
          />
        )}

        {deal?.contact_id && (
          <DirectCallModal
            isOpen={showDirectCallModal}
            onClose={() => setShowDirectCallModal(false)}
            isStarting={dailyRing.isStarting}
            onSelect={(userId) => {
              setShowDirectCallModal(false);
              dailyRing.startDailyRing({ contactId: deal.contact_id, dealId, targetUserId: userId });
            }}
          />
        )}

        {/* Cancel Deal Modal */}
        <CancelDealModal
          open={showCancelDealModal}
          onOpenChange={setShowCancelDealModal}
          dealId={dealId}
          dealName={deal?.name || items?.[0]?.catalog_item_name || 'Deal'}
          isLoading={cancelDealMutation.isPending}
          onConfirm={async ({ reason, cancelledAt, cancelPayments }) => {
            const { data: { user: authUser } } = await supabase.auth.getUser();
            const { data: appUser } = await supabase
              .from('app_users')
              .select('id')
              .eq('auth_user_id', authUser?.id)
              .single();

            await cancelDealMutation.mutateAsync({
              dealId,
              reason,
              cancelledBy: appUser?.id || authUser?.id || 'unknown',
              cancelPayments,
              cancelledAt
            });
            
            setShowCancelDealModal(false);
            await loadDeal();
          }}
        />

        {/* Revert Cancel Deal Modal */}
        <RevertCancelModal
          open={showRevertCancelModal}
          onOpenChange={setShowRevertCancelModal}
          onConfirm={async (revertPayments) => {
            await revertCancelMutation.mutateAsync({
              dealId,
              revertPayments
            });
            setShowRevertCancelModal(false);
            await loadDeal();
          }}
          isLoading={revertCancelMutation.isPending}
          cancelledPaymentsCount={cancelledPaymentsCount}
        />

        {/* Deal Closing Validation Modal */}
        <DealClosingValidation
          isOpen={showValidationDialog}
          onClose={() => {
            setShowValidationDialog(false);
            setPendingStage(null);
          }}
          dealId={dealId}
          onConfirm={() => {
            setShowValidationDialog(false);
            setClosedDateForWon(new Date());
            setShowWonDialog(true);
          }}
          onNavigateToItem={(itemId, issue) => {
            const subtab = issue === 'missing_eligibility' ? 'eligibility' : 'people';
            setItemSubtab(prev => ({ ...prev, [itemId]: subtab }));
            setActiveTab('details');
            setExpandedItemId(itemId);
            setTimeout(() => {
              const el = document.getElementById(`item-card-${itemId}`);
              el?.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }, 150);
          }}
        />

        {/* Card Setup Share Modal */}
        <CardSetupShareModal
          open={showCardSetupModal}
          onOpenChange={setShowCardSetupModal}
          cardSetupLink={cardSetupLink}
          contactName={contact ? `${contact.first_name || ''} ${contact.last_name || ''}`.trim() : undefined}
          contactPhone={contact?.primary_phone}
        />

        {/* Charge Adhoc Modal */}
        <ChargeAdhocModal
          open={showChargeModal}
          onOpenChange={setShowChargeModal}
          dealId={dealId}
          dealName={deal?.name}
          contactName={contact ? `${contact.first_name || ''} ${contact.last_name || ''}`.trim() : undefined}
          hasCard={!!(contact?.has_payment_method || contact?.stripe_default_payment_method_id || deal?.card_on_file)}
          onSuccess={async () => {
            await loadDeal();
            queryClient.invalidateQueries({ queryKey: ['payment-transactions-by-deal', dealId] });
            queryClient.invalidateQueries({ queryKey: ['payments', dealId] });
          }}
          onGenerateLink={handleGeneratePaymentLink}
        />

        {/* Payment Link Share Modal */}
        <PaymentLinkShareModal
          open={showPaymentLinkModal}
          onOpenChange={setShowPaymentLinkModal}
          paymentLink={paymentLink}
          amount={chargeAmount}
          contactName={contact ? `${contact.first_name || ''} ${contact.last_name || ''}`.trim() : undefined}
          contactPhone={contact?.primary_phone}
        />

        {/* Edit Title Dialog */}
        <Dialog open={showEditTitleDialog} onOpenChange={setShowEditTitleDialog}>
          <DialogContent className="sm:max-w-[425px]">
            <DialogHeader>
              <DialogTitle>{t('deals.editTitle') || 'Editar Título da Negociação'}</DialogTitle>
              <DialogDescription>
                {t('deals.editTitleDescription') || 'Altere o título/nome desta negociação'}
              </DialogDescription>
            </DialogHeader>
            <div className="py-4">
              <Label htmlFor="dealTitle">{t('deals.dealTitle') || 'Título'}</Label>
              <Input
                id="dealTitle"
                value={editingTitle}
                onChange={(e) => setEditingTitle(e.target.value)}
                placeholder={t('deals.enterDealTitle') || 'Digite o título da negociação'}
                className="mt-2"
              />
            </div>
            <div className="flex justify-end gap-2">
              <Button
                variant="outline"
                onClick={() => setShowEditTitleDialog(false)}
                disabled={isProcessing}
              >
                {t('common.cancel')}
              </Button>
              <Button
                onClick={async () => {
                  if (!editingTitle.trim()) {
                    toast.error(t('deals.titleRequired') || 'O título é obrigatório');
                    return;
                  }
                  try {
                    setIsProcessing(true);
                    const { error } = await supabase
                      .from('deals')
                      .update({ name: editingTitle.trim() })
                      .eq('id', dealId);
                    if (error) throw error;
                    await loadDeal();
                    setShowEditTitleDialog(false);
                    toast.success(t('deals.titleUpdated') || 'Título atualizado com sucesso');
                  } catch (error) {
                    console.error('Error updating title:', error);
                    toast.error(t('deals.errorUpdatingTitle') || 'Erro ao atualizar título');
                  } finally {
                    setIsProcessing(false);
                  }
                }}
                disabled={isProcessing || !editingTitle.trim()}
              >
                {isProcessing ? t('common.saving') : t('common.save')}
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Prospecting Validation Modal */}
        <ProspectingValidationModal
          isOpen={showProspectingValidation}
          onClose={() => setShowProspectingValidation(false)}
          dealId={dealId}
          contactId={contact?.id || ''}
          missingFields={prospectingMissingFields}
        />

        {/* NOTE: LostReasonModal removed - using unified showLostReasonDialog AlertDialog above */}

        <PostMeetingModal
          isOpen={showPostMeetingModal}
          onClose={() => setShowPostMeetingModal(false)}
          onConfirm={handleConfirmPostMeeting}
          dealId={dealId}
          isLoading={isProcessing}
        />

        <QualificationRequiredModal
          isOpen={showQualificationModal}
          onClose={() => setShowQualificationModal(false)}
          onConfirm={handleConfirmQualification}
          dealId={dealId}
          contactId={contact?.id}
          missingFields={qualificationMissingFields}
          isLoading={isProcessing}
        />

        {/* Full Qualification Modal - 6-step wizard */}
        <QualificationModal
          open={showFullQualificationModal}
          onClose={() => setShowFullQualificationModal(false)}
          dealId={dealId}
          contactId={contact?.id || null}
          contactName={contact ? `${contact.first_name || ''} ${contact.last_name || ''}`.trim() : ''}
          onComplete={async () => {
            await loadDeal();
            setShowFullQualificationModal(false);
            toast.success(t('qualification.completed') || 'Qualificação concluída!');
          }}
        />

        {showWhatsAppSheet && contact?.id && (
          <DrawerChatLayout onClose={() => setShowWhatsAppSheet(false)}>
            <ChatCore
              contactId={contact.id}
              contactPhone={getFullPhoneE164(contact.primary_phone, contact.primary_phone_country_code)}
              showConversationSelector
              header={
                <DealChatHeader
                  contactName={`${contact.first_name || ''} ${contact.last_name || ''}`.trim() || 'Contato'}
                  phoneNumber={formatPhoneNumber(contact.primary_phone, contact.primary_phone_country_code)}
                  isConnected
                  onClose={() => setShowWhatsAppSheet(false)}
                  onCall={() => {
                    const phone = getFullPhoneE164(contact.primary_phone, contact.primary_phone_country_code);
                    if (phone) {
                      window.dispatchEvent(new CustomEvent('initiate-call', {
                        detail: { contactId: contact.id, phoneNumber: phone }
                      }));
                    }
                  }}
                />
              }
            />
          </DrawerChatLayout>
        )}
      </div>
    </div>;
}
